import { ListProjectVariablesResponse } from 'api/hooks/useListProjectVariables/types'

import {
  JobVariable,
  JobVariableCollection,
  JobVariableType,
  JobVariableVisibility,
  VariableScope
} from 'job-lib/types/Variables'

import {
  ErrorStatuses,
  Fields,
  FormState,
  ProjectDefaultOverrides,
  ProjectVariable,
  ProjectVariableOverride,
  ReducerActions
} from 'modules/ManageVariables/types'
import { isJobVariable } from 'modules/ManageVariables/utils'

import { errorMessages } from '../errorMessages'
import { updateDefaultValue } from './updateDefaultValue'
import { updateName } from './updateName'
import { updateProjectOverride } from './updateProjectOverride'
import { updateValue } from './updateValue'
import { updateVariableType } from './updateVariableType'
import { validateForm } from './validateForm'

export type ReducerAction =
  | {
      type: ReducerActions.UPDATE_FIELD
      field: Fields.NAME
      value: string
    }
  | {
      type: ReducerActions.UPDATE_FIELD
      field: Fields.VARIABLE_SCOPE
      value: VariableScope | null
    }
  | {
      type: ReducerActions.UPDATE_FIELD
      field: Fields.VISIBILITY
      value: JobVariableVisibility | null
    }
  | {
      type: ReducerActions.UPDATE_FIELD
      field: Fields.DEFAULT_VALUE | Fields.DESCRIPTION
      value: string
    }
  | {
      type: ReducerActions.VALIDATE_FORM
    }
  | {
      type: ReducerActions.UPDATE_NAME
      value: string
      jobVariables: JobVariableCollection | undefined
      projectVariables: ListProjectVariablesResponse
      originalName?: string
    }
  | {
      type: ReducerActions.UPDATE_PROJECT_OVERRIDE
      field: Fields.PROJECT_DEFAULT_OVERRIDES
      value: ProjectVariableOverride['value']
      environmentId: ProjectVariableOverride['environmentId']
    }
  | {
      type: ReducerActions.UPDATE_DEFAULT_VALUE
      field: Fields.DEFAULT_VALUE
      value: string
    }
  | {
      type: ReducerActions.UPDATE_VARIABLE_TYPE
      field: Fields.VARIABLE_TYPE
      value: JobVariableType | null
    }

export const formReducer = (
  state: FormState,
  action: ReducerAction
): FormState => {
  switch (action.type) {
    case ReducerActions.UPDATE_FIELD:
      return updateValue(state, action)
    case ReducerActions.UPDATE_NAME:
      return updateName(state, action)
    case ReducerActions.VALIDATE_FORM:
      return validateForm(state)
    case ReducerActions.UPDATE_PROJECT_OVERRIDE:
      return updateProjectOverride(state, action)
    case ReducerActions.UPDATE_DEFAULT_VALUE:
      return updateDefaultValue(state, action)
    case ReducerActions.UPDATE_VARIABLE_TYPE:
      return updateVariableType(state, action)
    default:
      throw new Error('Please provide a valid action to formReducer')
  }
}

const initState: FormState = {
  [Fields.DEFAULT_VALUE]: {
    value: '',
    isValid: true,
    error: ''
  },
  [Fields.DESCRIPTION]: {
    value: '',
    isValid: true,
    error: ''
  },
  [Fields.NAME]: {
    value: '',
    isValid: null,
    error: errorMessages[ErrorStatuses.EMPTY]
  },
  [Fields.VARIABLE_TYPE]: {
    value: null,
    isValid: null,
    error: errorMessages[ErrorStatuses.SELECT_OPTION]
  },
  [Fields.VARIABLE_SCOPE]: {
    value: VariableScope.JOB_VARIABLE,
    isValid: true,
    error: ''
  },
  [Fields.VISIBILITY]: {
    value: null,
    isValid: null,
    error: errorMessages[ErrorStatuses.SELECT_OPTION]
  },
  [Fields.PROJECT_DEFAULT_OVERRIDES]: {
    value: {},
    isValid: true,
    error: ''
  },
  isFormValid: false
}

export const getInitialState = (
  selectedVariableScope: VariableScope,
  variableToEdit?: JobVariable | ProjectVariable
): FormState => {
  if (!variableToEdit) {
    return {
      ...initState,
      [Fields.VARIABLE_SCOPE]: {
        ...initState[Fields.VARIABLE_SCOPE],
        value: selectedVariableScope
      }
    }
  } else {
    if (isJobVariable(variableToEdit)) {
      return getInitialStateForJobVariable(variableToEdit)
    } else {
      return getInitialStateForProjectVariable(variableToEdit)
    }
  }
}

function getInitialStateForJobVariable(variable: JobVariable): FormState {
  const { definition, value } = variable
  const { name, type, visibility, description } = definition
  return {
    ...initState,
    isFormValid: true,
    [Fields.DEFAULT_VALUE]: {
      ...initState[Fields.DEFAULT_VALUE],
      value: value,
      isValid: true
    },
    [Fields.DESCRIPTION]: {
      ...initState[Fields.DESCRIPTION],
      value: description ?? '',
      isValid: true
    },
    [Fields.NAME]: { ...initState[Fields.NAME], value: name, isValid: true },
    [Fields.VARIABLE_SCOPE]: {
      ...initState[Fields.VARIABLE_SCOPE],
      value: VariableScope.JOB_VARIABLE,
      isValid: true
    },
    [Fields.VARIABLE_TYPE]: {
      ...initState[Fields.VARIABLE_TYPE],
      value: type,
      isValid: true
    },
    [Fields.VISIBILITY]: {
      ...initState[Fields.VISIBILITY],
      value: visibility,
      isValid: true
    }
  }
}

function getInitialStateForProjectVariable(
  variable: ProjectVariable
): FormState {
  const { description, name, overrides, type, value } = variable
  return {
    ...initState,
    isFormValid: true,
    [Fields.DEFAULT_VALUE]: {
      ...initState[Fields.DEFAULT_VALUE],
      value: value ? value.toString() : '',
      isValid: true
    },
    [Fields.DESCRIPTION]: {
      ...initState[Fields.DESCRIPTION],
      value: description,
      isValid: true
    },
    [Fields.NAME]: { ...initState[Fields.NAME], value: name, isValid: true },
    [Fields.PROJECT_DEFAULT_OVERRIDES]: {
      ...initState[Fields.PROJECT_DEFAULT_OVERRIDES],
      value: overrides.reduce<ProjectDefaultOverrides>((accumulator, next) => {
        return {
          ...accumulator,
          [next.environmentId]: {
            environmentId: next.environmentId,
            value: next.value,
            isValid: true,
            error: ''
          }
        }
      }, {}),
      isValid: true
    },
    [Fields.VARIABLE_SCOPE]: {
      ...initState[Fields.VARIABLE_SCOPE],
      value: VariableScope.PROJECT_VARIABLE,
      isValid: true
    },
    [Fields.VARIABLE_TYPE]: {
      ...initState[Fields.VARIABLE_TYPE],
      value: type,
      isValid: true
    }
  }
}
